import { css } from "@emotion/react";
import { Suspense, useCallback } from "react";
import type { Key } from "react-aria-components";
import { Collection } from "react-aria-components";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

import { Loading, Tab, TabList, TabPanel, Tabs } from "@phoenix/components";
import { useViewerCanManageSandboxes } from "@phoenix/contexts";

const settingsPageCSS = css`
  overflow-y: auto;
  height: 100%;
`;

const settingsPageInnerCSS = css`
  padding: var(--global-dimension-size-100);
  max-width: 1300px;
  min-width: 500px;
  box-sizing: border-box;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const TABS: { id: string; label: string }[] = [
  { id: "general", label: "General" },
  { id: "providers", label: "AI Providers" },
  { id: "sandboxes", label: "Sandboxes" },
  { id: "models", label: "Models" },
  { id: "secrets", label: "Secrets" },
  { id: "datasets", label: "Datasets" },
  { id: "annotations", label: "Annotations" },
  { id: "prompts", label: "Prompts" },
  { id: "data", label: "Data Retention" },
  { id: "agents", label: "Assistant" },
];

export function SettingsPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = pathname.split("/settings")[1].replace("/", "");
  const onChangeTab = useCallback(
    (tab: Key) => {
      if (typeof tab === "string") {
        navigate(`/settings/${tab}`, { replace: true });
      }
    },
    [navigate]
  );
  const isAgentAssistantEnabled = !window.Config.agentAssistantDisabled;
  const canManageSecrets = useViewerCanManageSandboxes();
  const canManageSandboxes = useViewerCanManageSandboxes();
  const tabs = TABS.filter((tab) => {
    if (tab.id === "agents" && !isAgentAssistantEnabled) {
      return false;
    }
    if (tab.id === "secrets" && !canManageSecrets) {
      return false;
    }
    if (tab.id === "sandboxes" && !canManageSandboxes) {
      return false;
    }
    return true;
  });
  if (!tab) {
    return <Navigate to="/settings/general" replace />;
  }
  if (!tabs.some((item) => item.id === tab)) {
    return <Navigate to="/settings/general" replace />;
  }
  return (
    <main css={settingsPageCSS}>
      <div css={settingsPageInnerCSS}>
        <Tabs selectedKey={tab} onSelectionChange={onChangeTab}>
          {/* TODO: filter sandboxes tab for non-admins */}
          <TabList items={tabs}>
            {(item) => <Tab id={item.id}>{item.label}</Tab>}
          </TabList>
          <Collection items={tabs}>
            {(item) => (
              <TabPanel id={item.id} padded>
                <Suspense fallback={<Loading />}>
                  <Outlet />
                </Suspense>
              </TabPanel>
            )}
          </Collection>
        </Tabs>
      </div>
    </main>
  );
}
